/**
 * Pure re-pricing engine for the composition editor's market date (MDOT-25191): what the editor
 * asks the pricing endpoint about, and what its answer does to the grid.
 *
 * `holdingMode` lands as three row states, and they are the whole model:
 *
 * | `holdingMode`             | price      | market value   | weight         | row            |
 * |---------------------------|------------|----------------|----------------|----------------|
 * | `SHARES`                  | the answer | Cold War's     | Cold War's     | editable       |
 * | `WEIGHT`                  | cleared    | kept as it was | kept as it was | sized by money |
 * | `UNPRICED`, or unanswered | cleared    | cleared        | cleared        | disabled       |
 *
 * A `WEIGHT` row — a nested portfolio, an index, a live proxy — is held by weight rather than by
 * shares, so it has no price to take at a day and the value it already carries is the only one it
 * has. This answer marks a row `heldByValue` (MDOT-25244), and drops any share count it carried
 * — as a loaded row with no quantity, or an added one held by value by its nature, already is
 * (`../heldByValue`) — which is what lets the editor size it in money instead of in shares: a missing price says the same about a share row that is not priced yet, so it cannot
 * stand in for the answer. An `UNPRICED` row has no
 * value at that day; cash absorbs the gap, which is why the grid marks the row and blocks submit
 * rather than quietly reading a cash position that is too high.
 *
 * What the engine prices at is the latest close **at or before** the date asked about: when that
 * day has no close of its own — a weekend, a holiday, an instrument that did not trade — it walks
 * back up to 90 days and answers the last one before it. Today is that same rule rather than a case
 * of its own, today's close not existing yet. So `UNPRICED` does not mean the market was shut that
 * day: it means no close in the 90 days to it. That is why the way out for an unpriced row is an
 * **earlier** date — a later one only walks back from further away, and today is the furthest point
 * of all.
 *
 * An answer that priced a row is also kept ON the row, under the day it answered about
 * (`pricingByDate`): the rows are then the whole of what the editor knows about prices, at the day
 * on screen and at every day visited before it. {@link planPricing} reads that memo back, so a day
 * already answered for is re-projected from the rows and a call goes out for the rest alone —
 * which is what stops one failed call from striking out a grid that was priced a moment ago.
 *
 * A price is not a value: what a `SHARES` row is worth at the day is Cold War's, asked by sizing the
 * row by its own shares once the price has landed (`../position-sizing/valueByDate`). Until then the
 * row keeps the value it had, unless it was already given one at that day for the shares it holds.
 *
 * Nothing here calls the API or touches the builder: it maps rows to identifiers and an answer to
 * rows. All arithmetic goes through `../derive`, so the quantity invariants hold on a reprice
 * exactly as they do on an edit.
 */
import type { InstrumentPricing } from "@eu.mdotm.api/sphere";
import type { Instrument } from "../../const";
import type { QuantityEditResult } from "../derive";
import { computeCashMarketValue, deriveWeight } from "../derive";
import { rememberedValue } from "../position-sizing/valueByDate";

/** What the engine answered about each requested identifier. */
export type PricingByIdentifier = Map<string, InstrumentPricing>;

/**
 * How a row names itself to the pricing endpoint: `identifiers` is documented as "ticker names, or
 * the uuid of a nested portfolio", so a nested portfolio goes by its uuid and everything else by
 * its ticker. A row that can answer neither cannot be asked about, and is left alone.
 */
export function pricingIdentifier(row: Instrument): string | undefined {
	return row.investmentUuid ?? row.tickerName;
}

/**
 * The identifiers one price call asks about: every row but cash, which has no price by nature and
 * whose market value is the derived AUM remainder. Soft-deleted rows ARE asked about, so a row
 * restored after a date change is priced like the rest of the grid instead of at the previous day.
 */
export function pricingIdentifiers(rows: Iterable<Instrument>): string[] {
	const identifiers = new Set<string>();
	for (const row of rows) {
		if (row.isCash) {
			continue;
		}
		const identifier = pricingIdentifier(row);
		if (identifier) {
			identifiers.add(identifier);
		}
	}
	return Array.from(identifiers);
}

/** Every requested identifier marked unpriced — what a failed call means (MDOT-25191 decision 2). */
export function allUnpriced(identifiers: Iterable<string>): PricingByIdentifier {
	return new Map(
		Array.from(identifiers, (identifier) => [identifier, { identifier, holdingMode: "UNPRICED" } as const]),
	);
}

/**
 * Whether an answer priced the row — the two arms of {@link applyPricing} that leave a row with a
 * value at the date. Only these are worth remembering: an `UNPRICED` row may well be priceable a
 * minute later, and a `SHARES` answer carrying no number is no price at all.
 */
export function isPricedAnswer(answer: InstrumentPricing): boolean {
	return (answer.holdingMode === "SHARES" && answer.price != null) || answer.holdingMode === "WEIGHT";
}

/** The answer a row already carries for `date`, if it was priced there earlier in this edit. */
function rememberedPricing(row: Instrument, date: string): InstrumentPricing | undefined {
	return row.pricingByDate?.[date];
}

/** The row's memo with `answer` written under `date`, or the memo untouched when it is no answer. */
function withRememberedPricing(row: Instrument, date: string, answer: InstrumentPricing): Instrument["pricingByDate"] {
	if (!isPricedAnswer(answer)) {
		return row.pricingByDate;
	}
	return { ...row.pricingByDate, [date]: answer };
}

/**
 * A row as it was loaded, with the price it arrived carrying remembered under `date` (MDOT-25191).
 *
 * A composition arrives priced at its latest close, which is the same thing the pricing engine
 * answers for today — so today is an answered day from the first render: coming back to it after a
 * trip through other days costs no call, and cannot be struck out by one that fails.
 *
 * Only a row with a unit price is remembered. A row carrying none may be a nested portfolio, an
 * index or a live proxy, and `WEIGHT` is the engine's answer to give — assuming it here would mark
 * the row held by value on nothing but the absence of a number. An answer already remembered for
 * the day wins: it came from the engine, this is an inference.
 */
export function rememberLoadedPricing(row: Instrument, date: string): Instrument {
	const identifier = pricingIdentifier(row);
	if (row.isCash || !identifier || row.price == null || row.pricingByDate?.[date]) {
		return row;
	}
	return {
		...row,
		pricingByDate: { ...row.pricingByDate, [date]: { identifier, holdingMode: "SHARES", price: row.price } },
	};
}

/** What a reprice at a day already knows, and what it still has to ask about. */
export type PricingPlan = {
	/** Answers the rows already carry for the day — no call needed for these. */
	known: PricingByIdentifier;
	/** The identifiers with no answer for the day yet: exactly what one call asks about. */
	missing: string[];
};

/**
 * Splits the composition at `date` into what the rows already know and what the engine still has to
 * be asked (MDOT-25191). Rows the memo covers cost no call and, with it, cannot be turned unpriced
 * by a call that fails — the failure lands on the rows that had no answer to begin with.
 */
export function planPricing(rows: Iterable<Instrument>, date: string): PricingPlan {
	const known: PricingByIdentifier = new Map();
	const missing = new Set<string>();
	for (const row of rows) {
		if (row.isCash) {
			continue;
		}
		const identifier = pricingIdentifier(row);
		if (!identifier) {
			continue;
		}
		const answer = rememberedPricing(row, date);
		if (answer) {
			known.set(identifier, answer);
		} else {
			missing.add(identifier);
		}
	}
	return { known, missing: Array.from(missing) };
}

/**
 * The response read against what was asked: an identifier the engine did not answer about is
 * unpriced, so a short response cannot leave stale prices standing under a new date.
 */
export function pricingFromResponse(instruments: Iterable<InstrumentPricing>, requested: Iterable<string>) {
	const pricing = allUnpriced(requested);
	for (const instrument of instruments) {
		if (instrument.identifier) {
			pricing.set(instrument.identifier, instrument);
		}
	}
	return pricing;
}

/**
 * Applies an answer to the composition: each row takes the state its `holdingMode` names, and the
 * cash row absorbs whatever the priced rows no longer claim (I1), keeping the weights summing to
 * 100 % (I3).
 *
 * A row nobody asked about — one with no identifier — is passed through untouched: there is no
 * answer about it either way, and blanking it would be inventing one.
 *
 * An answer that priced a row is remembered on it under `date`, so the same day costs no second
 * call. An `UNPRICED` one is not: the row's memo keeps whatever other days it holds, and that day
 * is asked about again next time.
 */
export function applyPricing(params: {
	rows: Instrument[];
	pricing: PricingByIdentifier;
	aum: number;
	/** The day the answer is about — the key the priced rows are remembered under. */
	date: string;
}): QuantityEditResult {
	const { rows, pricing, aum, date } = params;

	const repriced = rows.map((row) => {
		if (row.isCash) {
			return row;
		}
		const identifier = pricingIdentifier(row);
		const answer = identifier ? pricing.get(identifier) : undefined;
		if (!identifier || !answer) {
			return row;
		}
		// A `SHARES` answer without a number is no price at all, so it takes the unpriced arm rather
		// than pricing the row at zero.
		if (answer.holdingMode === "SHARES" && answer.price != null) {
			// A row nobody has sized yet stays blank: the price lands, the value waits on a quantity. A sized
			// row takes what Cold War gave it at this day when it already has; until then it shows its old
			// value, weighed against the AUM, as a placeholder the valuation replaces.
			const remembered = row.quantity == null ? undefined : rememberedValue(row, date, aum);
			const marketValue = row.quantity == null ? undefined : (remembered?.marketValue ?? row.marketValue);
			return {
				...row,
				unpriced: false,
				heldByValue: false,
				price: answer.price,
				marketValue,
				weight: remembered?.weight ?? (marketValue == null ? undefined : deriveWeight(marketValue, aum)),
				pricingByDate: withRememberedPricing(row, date, answer),
			};
		}
		if (answer.holdingMode === "WEIGHT") {
			// Neither the day nor a price moves what a row held by value is worth, so its value and the
			// weight Cold War gave it stand.
			return {
				...row,
				unpriced: false,
				heldByValue: true,
				// A row held by value has no share count: the save refuses one on it.
				quantity: undefined,
				price: undefined,
				pricingByDate: withRememberedPricing(row, date, answer),
			};
		}
		return {
			...row,
			unpriced: true,
			heldByValue: false,
			price: undefined,
			marketValue: undefined,
			weight: undefined,
		};
	});

	const cashMarketValue = computeCashMarketValue(repriced, aum);
	return {
		rows: repriced.map((row) => {
			if (row.isCash) {
				return { ...row, marketValue: cashMarketValue, weight: deriveWeight(cashMarketValue, aum) };
			}
			return row;
		}),
		cashMarketValue,
	};
}

/** Whether the editor could not price the row at the market date. */
export function isUnpriced(row: Instrument): boolean {
	return Boolean(row.unpriced);
}

/**
 * How many rows the editor could not price. Soft-deleted rows do not count: they leave the
 * composition at save, so they cannot block it.
 */
export function unpricedCount(rows: Iterable<Instrument>): number {
	let count = 0;
	for (const row of rows) {
		if (isUnpriced(row) && !row.deleted && !row.isCash) {
			count += 1;
		}
	}
	return count;
}

/** True while the composition carries a row with no price — submit must be blocked. */
export function hasUnpricedRows(rows: Iterable<Instrument>): boolean {
	return unpricedCount(rows) > 0;
}
